import React from 'react'
import { Head, usePage } from '@inertiajs/react'
import FlashMessages from '@/components/FlashMessages'
import Header from './Header'
import Sidebar from './Sidebar'
import { getSectionLabelFromUrl } from './menuItems'

interface EventLayoutProps {
  children: React.ReactNode
  className?: string
}

interface EventContext {
  id?: number
  name?: string
  slug?: string
  status?: string
}

interface SharedProps {
  current_user?: {
    id: number
    email: string
    name?: string
    global_admin: boolean
    event_role?: 'owner' | 'crew' | null
  }
  /** Shared on every event page via `Events::BaseController#inertia_share`. */
  event_context?: EventContext
  /** Some pages expose the event directly as their own prop. */
  event?: { name?: string; slug?: string }
}

export default function EventLayout({ children, className }: EventLayoutProps) {
  const { props, url } = usePage<SharedProps>()
  const currentUser = props.current_user

  // Build the document title as "<Section> - <Event Name>" (e.g.
  // "Attendee Management - Xpert Symposium 2026") so the browser tab is
  // meaningful instead of showing the raw URL. The active section label reuses
  // the sidebar's url -> label mapping (single source of truth in menuItems).
  // A page may still override this with its own <Head title>, which wins because
  // it renders after this <Head> and Inertia keeps the last-registered title.
  const eventName = props.event_context?.name || props.event?.name || 'Event'
  const eventSlug = props.event_context?.slug || props.event?.slug || url.split('/')[2] || ''
  const sectionLabel = getSectionLabelFromUrl(url, eventSlug)
  const documentTitle = sectionLabel ? `${sectionLabel} - ${eventName}` : eventName

  // Determine user role to display
  const getUserRole = () => {
    if (!currentUser) return undefined
    if (currentUser.global_admin) return 'Admin'
    if (currentUser.event_role === 'owner') return 'Event Owner'
    if (currentUser.event_role === 'crew') return 'Event Crew'
    return 'Event Owner' // Fallback
  }

  return (
    <div className={`min-h-screen flex flex-col bg-gray-100 ${className}`}>
      <Head title={documentTitle} />
      <FlashMessages />

      {/* Header - Fixed at top */}
      <Header
        loggedInUser={
          currentUser
            ? {
                name: currentUser.name || currentUser.email,
                role: getUserRole(),
                email: currentUser.email,
              }
            : undefined
        }
      />

      {/* Sidebar - Fixed at left */}
      <Sidebar />

      {/* Main Content Area */}
      <main className="flex-1 flex flex-col transition-all duration-300 ease-in-out ms-64">
        {children}
      </main>
    </div>
  )
}
